// MIRRORS: panel/util/phone.ts — identical copy; edit both, or delete this line on both to declare a deliberate split.

/**
 * Turkish mobile numbers, in the three shapes the app needs them.
 *
 *   stored / sent   +905551234567      (E.164, what the API takes)
 *   typed           5551234567         (ten digits, what the user keys)
 *   shown           +90 (555) 555 55 55
 *
 * The country code is never typed and never stored separately: every number
 * here is Turkish by constraint, so +90 is chrome, not data.
 */

/** A Turkish mobile is ten digits and always begins with 5. */
export const PHONE_DIGITS = 10;

/**
 * Pulls the subscriber digits out of anything a person might paste — `0555…`,
 * `+90 555…`, `90555…`, `(555) 555 55 55` — and keeps whatever is left even if
 * it is wrong, so the caller can say why rather than silently eating input.
 *
 * ⚠️ Deliberately does NOT truncate. It used to cut at ten, which meant an
 * eleven-digit number was accepted as the ten-digit one hiding inside it: you
 * could log in, register, or confirm an account deletion with a number you had
 * not typed, and nothing downstream could tell, because what left the browser
 * was a well-formed number. Length is an input-field concern (see PhoneField's
 * cap); what a string *means* has to be lossless.
 */
export function toPhoneDigits(raw: string): string {
  let digits = raw.replace(/\D/g, '');
  if (digits.startsWith('90')) digits = digits.slice(2);
  else if (digits.startsWith('0')) digits = digits.slice(1);
  return digits;
}

/** Ten digits beginning with 5. Anything else is refused before the API sees it. */
export const isValidPhoneDigits = (digits: string): boolean =>
  new RegExp(`^5\\d{${PHONE_DIGITS - 1}}$`).test(digits);

/**
 * "5551234567" → "(555) 555 55 55", formatting only as far as the digits go so
 * the caret does not jump ahead of what has been typed.
 */
export function formatPhoneDigits(digits: string): string {
  const d = digits.slice(0, PHONE_DIGITS);
  if (d.length <= 3) return d.length ? `(${d}` : '';
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  if (d.length <= 8) return `(${d.slice(0, 3)}) ${d.slice(3, 6)} ${d.slice(6)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)} ${d.slice(6, 8)} ${d.slice(8)}`;
}

/** E.164 → "+90 (555) 555 55 55". Anything unrecognised is returned untouched. */
export function formatPhoneDisplay(e164: string | null | undefined): string {
  if (!e164) return '';
  const digits = toPhoneDigits(e164);
  if (!isValidPhoneDigits(digits)) return e164;
  return `+90 ${formatPhoneDigits(digits)}`;
}

/**
 * Anything a user typed → "+905551234567" per the backend's E.164 schema.
 * Returns null when the input cannot be a Turkish mobile — callers surface a
 * field error instead of letting the API 400.
 */
export function normalizePhone(raw: string): string | null {
  const digits = toPhoneDigits(raw);
  return isValidPhoneDigits(digits) ? `+90${digits}` : null;
}
